import React, { useState } from 'react';
import { can } from '../lib/access';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { FileText, Clock, User, Tag, AlertTriangle, Upload, ChevronDown, ChevronRight, Shield, BookOpen, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import { SUPPORTED_UPLOAD_ACCEPT } from '../utils/supportedUploadFormats';
import KnowledgeBadgePicker from './KnowledgeBadgePicker';
import VersionHistory from './VersionHistory';
import LinkedSourceAccessFields from './LinkedSourceAccessFields';
import LinkedSourcePreview from './LinkedSourcePreview';
import TeamHierarchyPicker from './TeamHierarchyPicker';
import ReviewScheduleFields, { EMPTY_REVIEW_SCHEDULE, reviewScheduleFromItem, reviewSchedulePayload } from './ReviewScheduleFields';

const HoverHint = ({ label, children }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" aria-label={label} className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700">
          <Info size={12} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">{children}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const KnowledgeEditor = ({ item, open, onOpenChange, teams, categories = [], onSave, user, initialVersionId }) => {
  const [fields, setFields] = useState({
    title: '', description: '', keywords: '', resolution_steps: [],
    email_template: '', contacts: '', ticket_reference: '',
    sla_hours: '', sla_details: '', owner: '', department: '',
    status: 'draft', team_id: '', category_id: '', source_type_name: '', simple_answer: '',
    source_type: 'static', source_url: '', refresh_interval: 'manual_check_only',
    extraction_scope: 'full_page', custom_selector: '',
    source_auth_method: 'public', source_auth: {},
    badge_ids: [], source_published_at: '', effective_from: '', effective_until: '',
    last_reviewed_at: '', version_label: '', review_schedule: EMPTY_REVIEW_SCHEDULE,
  });
  const [showRawContent, setShowRawContent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [testingLinkedFetch, setTestingLinkedFetch] = useState(false);
  const [linkedFetchState, setLinkedFetchState] = useState(null);
  const [showExtractionAdvanced, setShowExtractionAdvanced] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [teamPickerOpen, setTeamPickerOpen] = useState(false);

  // Reset fields when item changes
  React.useEffect(() => {
    if (item) {
      const legacyCategoryId = !item.category_id && categories.some(category => category.id === item.team_id)
        ? item.team_id
        : '';
      setFields({
        title: item.title || '',
        description: item.description || '',
        keywords: item.keywords?.join(', ') || '',
        resolution_steps: item.resolution_steps || [],
        email_template: item.email_template || '',
        contacts: item.contacts?.join(', ') || '',
        ticket_reference: item.ticket_reference || '',
        sla_hours: item.sla_hours || '',
        sla_details: item.sla_details || '',
        owner: item.owner || '',
        department: item.department || '',
        status: item.status || 'draft',
        team_id: legacyCategoryId ? '' : (item.team_id || ''),
        category_id: item.category_id || legacyCategoryId,
        source_type_name: item.source_type_name || '',
        simple_answer: item.simple_answer || '',
        visibility: item.visibility || 'public',
        allowed_roles: (item.allowed_roles || []).join(', '),
        allowed_teams: (item.allowed_teams || []).join(', '),
        source_type: item.source_type || 'static',
        source_url: item.source_url || '',
        refresh_interval: item.refresh_interval || 'manual_check_only',
        extraction_scope: item.extraction_scope || 'full_page',
        custom_selector: item.custom_selector || '',
        source_auth_method: item.source_auth_method || 'public',
        source_auth: {},
        badge_ids: item.badge_ids || [],
        source_published_at: item.source_published_at || '',
        effective_from: item.effective_from || '',
        effective_until: item.effective_until || '',
        last_reviewed_at: item.last_reviewed_at || '',
        version_label: item.version_label || '',
        review_schedule: reviewScheduleFromItem(item.review_schedule),
      });
      setLinkedFetchState(null);
      setShowRawContent(false);
      setShowExtractionAdvanced(item.extraction_scope === 'custom_selector');
      setDatesOpen(false);
      setTeamPickerOpen(false);
    }
  }, [item, categories]);

  if (!item) return null;

  const canManageLinkedSources = can(user, 'can_manage_knowledge');
  const isIngested = !!(item.full_content || item.chunks?.length > 0);
  const isUploadedDocument = !!(item.ingestion_metadata?.original_filename || item.source_file || item.filename);
  const sourceFile = item.source_file || item.filename || null;
  const lastUpdated = item.updated_at || item.created_at || null;
  const hasEnteredSourceAuth = Object.values(fields.source_auth || {}).some(value => String(value || '').trim());
  const linkedSettingsChanged = fields.source_type === 'linked' && (
    item.source_type !== 'linked'
    || fields.source_url !== (item.source_url || '')
    || fields.extraction_scope !== (item.extraction_scope || 'full_page')
    || fields.custom_selector !== (item.custom_selector || '')
    || fields.source_auth_method !== (item.source_auth_method || 'public')
    || hasEnteredSourceAuth
  );

  const updateField = (key, value) => {
    setFields(prev => ({ ...prev, [key]: value }));
  };

  const handleCategorySelection = (categoryId) => {
    const category = categories.find(item => item.id === categoryId);
    setFields(prev => {
      const next = { ...prev, category_id: categoryId };
      if (!category?.settings_configured) return next;
      return {
        ...next,
        team_id: category.default_team_id || '',
        visibility: category.default_visibility || 'public',
        allowed_roles: (category.default_allowed_roles || []).join(', '),
        allowed_teams: (category.default_allowed_teams || []).join(', '),
      };
    });
  };

  const updateStep = (index, value) => {
    const updated = [...fields.resolution_steps];
    updated[index] = value;
    setFields(prev => ({ ...prev, resolution_steps: updated }));
  };

  const addStep = () => {
    setFields(prev => ({ ...prev, resolution_steps: [...prev.resolution_steps, ''] }));
  };

  const removeStep = (index) => {
    setFields(prev => ({ ...prev, resolution_steps: prev.resolution_steps.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    if (linkedSettingsChanged && !linkedFetchState?.success) {
      toast.error('Test and preview the updated document source before saving it.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: fields.title,
        description: fields.description,
        keywords: fields.keywords.split(',').map(k => k.trim()).filter(Boolean),
        resolution_steps: fields.resolution_steps.filter(s => s.trim()),
        email_template: fields.email_template || null,
        contacts: fields.contacts.split(',').map(c => c.trim()).filter(Boolean),
        ticket_reference: fields.ticket_reference,
        sla_hours: fields.sla_hours ? parseFloat(fields.sla_hours) : null,
        sla_details: fields.sla_details,
        owner: fields.owner,
        department: fields.department,
        status: fields.status,
        team_id: fields.team_id || null,
        category_id: fields.category_id || null,
        source_type_name: fields.source_type_name || null,
        simple_answer: fields.simple_answer || null,
        visibility: fields.visibility || 'public',
        allowed_roles: fields.visibility === 'restricted'
          ? fields.allowed_roles.split(',').map(r => r.trim()).filter(Boolean)
          : [],
        allowed_teams: fields.visibility === 'restricted'
          ? fields.allowed_teams.split(',').map(t => t.trim()).filter(Boolean)
          : [],
        badge_ids: fields.badge_ids || [],
        source_published_at: fields.source_published_at || null,
        effective_from: fields.effective_from || null,
        effective_until: fields.effective_until || null,
        last_reviewed_at: fields.last_reviewed_at || null,
        version_label: fields.version_label || null,
        review_schedule: reviewSchedulePayload(fields.review_schedule),
        ...(canManageLinkedSources ? {
          source_type: fields.source_type,
          source_url: fields.source_type === 'linked' ? fields.source_url : null,
          refresh_interval: fields.source_type === 'linked' ? fields.refresh_interval : 'manual_check_only',
          extraction_scope: fields.source_type === 'linked' ? fields.extraction_scope : 'full_page',
          custom_selector: fields.source_type === 'linked' && fields.extraction_scope === 'custom_selector' ? fields.custom_selector : null,
          source_auth_method: fields.source_type === 'linked' ? fields.source_auth_method : 'public',
          ...(fields.source_type === 'linked' && hasEnteredSourceAuth ? { source_auth: fields.source_auth } : {}),
        } : {}),
      };
      await axiosInstance.put(`/resolutions/${item.id}`, payload);
      toast.success('Knowledge item updated');
      if (onSave) onSave();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTestLinkedFetch = async () => {
    if (!fields.source_url.trim()) { toast.error('Enter a URL first'); return; }
    setTestingLinkedFetch(true);
    setLinkedFetchState(null);
    try {
      const response = await axiosInstance.post('/resolutions/linked-source/test-fetch', {
        source_url: fields.source_url.trim(),
        extraction_scope: fields.extraction_scope || 'full_page',
        custom_selector: fields.extraction_scope === 'custom_selector' ? fields.custom_selector : null,
        source_auth_method: fields.source_auth_method || 'public',
        source_auth: hasEnteredSourceAuth ? fields.source_auth : null,
        resolution_id: item.id,
      });
      setLinkedFetchState({ success: true, ...response.data });
      toast.success(`Fetch succeeded (${response.data.content_type})`);
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Fetch failed';
      setLinkedFetchState({ success: false, message });
      toast.error(message);
    } finally { setTestingLinkedFetch(false); }
  };

  const handleRefreshNow = async () => {
    setTestingLinkedFetch(true);
    try {
      const response = await axiosInstance.post(`/resolutions/${item.id}/refresh-linked`);
      toast.success(
        response.data.status === 'change_pending_review'
          ? 'A change was found and saved for review. The approved source is still active.'
          : 'Source checked; no changes found'
      );
      if (onSave) onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Refresh failed');
    } finally { setTestingLinkedFetch(false); }
  };

  const handleReplace = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReplacing(true);
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      const response = await axiosInstance.post(`/resolutions/${item.id}/replace-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const result = response.data;
      toast.success(`Source replaced and indexed · ${result.extracted_chars || 0} characters · ${result.chunk_count || 0} chunks`);
      if (onSave) onSave();
      onOpenChange(false);    } catch (error) {
      toast.error('Replace failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setReplacing(false);
    }
  };

  const teamName = teams?.find(t => t.id === item.team_id)?.name;
  const categoryLabel = (category) => {
    const names = [category.name];
    const seen = new Set([category.id]);
    let parent = categories.find(candidate => candidate.id === category.parent_id);
    while (parent && !seen.has(parent.id)) {
      names.unshift(parent.name);
      seen.add(parent.id);
      parent = categories.find(candidate => candidate.id === parent.parent_id);
    }
    return names.join(' / ');
  };
  const rawContent = item.full_content || item.chunks?.join('\n\n') || null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gray-50">
          <DialogTitle className="text-lg font-semibold text-gray-900" data-testid="knowledge-editor-title">
            {isUploadedDocument ? 'Edit Uploaded Document' : 'Edit Knowledge Item'}
          </DialogTitle>
          <DialogDescription className="sr-only">Edit the fields of this knowledge item</DialogDescription>

          {/* Source Metadata Bar */}
          <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-gray-500">
            {sourceFile && (
              <span className="flex items-center gap-1.5 bg-white border px-2.5 py-1 rounded">
                <FileText size={12} className="text-gray-400" />
                {sourceFile}
              </span>
            )}
            {isIngested && (
              <span className="flex items-center gap-1.5 bg-blue-50 text-blue-600 border border-blue-200 px-2.5 py-1 rounded">
                <BookOpen size={12} />
                Ingested Document
              </span>
            )}
            {isUploadedDocument && item.ingestion_metadata?.page_count != null && (
              <span className="flex items-center gap-1.5 bg-white border px-2.5 py-1 rounded">
                <FileText size={12} className="text-gray-400" />
                {item.ingestion_metadata.page_count} {item.ingestion_metadata.file_type === '.pptx' ? 'slides' : 'pages'}
              </span>
            )}
            {isUploadedDocument && (item.chunk_count ?? item.ingestion_metadata?.chunk_count) != null && (
              <span className="flex items-center gap-1.5 bg-white border px-2.5 py-1 rounded">
                <BookOpen size={12} className="text-gray-400" />
                {item.chunk_count ?? item.ingestion_metadata.chunk_count} indexed chunks
              </span>
            )}
            {lastUpdated && (
              <span className="flex items-center gap-1.5">
                <Clock size={12} />
                Updated: {new Date(lastUpdated).toLocaleDateString()}
              </span>
            )}
            <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded ${
              fields.status === 'approved' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}>
              <Shield size={12} />
              {fields.status === 'approved' ? 'Approved' : 'Draft'}
            </span>
          </div>
        </DialogHeader>

        <div className="px-6 py-5 space-y-5">
          {isUploadedDocument && fields.status !== 'approved' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="document-approval-warning">
              <strong>Not approved:</strong> this document is indexed, but approved status gives it authority in answers. Review the extracted content, then set Status to Approved when it is ready for use.
            </div>
          )}
          {/* Title */}
          <div>
            <Label className="text-sm font-medium text-gray-700">Title</Label>
            <Input
              value={fields.title}
              onChange={(e) => updateField('title', e.target.value)}
              className="mt-1"
              data-testid="editor-title"
            />
          </div>

          <div>
            <Label className="text-sm font-medium text-gray-700">Document type</Label>
            <Input
              value={fields.source_type_name}
              onChange={(e) => updateField('source_type_name', e.target.value)}
              placeholder="e.g. Process guide"
              className="mt-1"
              data-testid="editor-source-type-name"
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-sm font-medium text-gray-700">Description</Label>
            <Textarea
              value={fields.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={3}
              className="mt-1 text-sm"
              data-testid="editor-description"
            />
          </div>

          {/* Keywords */}
          <div>
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
              <Tag size={13} /> Keywords
            </Label>
            <Input
              value={fields.keywords}
              onChange={(e) => updateField('keywords', e.target.value)}
              placeholder="comma-separated keywords"
              className="mt-1 text-sm"
              data-testid="editor-keywords"
            />
            <p className="text-xs text-gray-400 mt-1">These drive AI matching. Use specific terms your team would search for.</p>
          </div>

          <KnowledgeBadgePicker
            value={fields.badge_ids}
            onChange={(badgeIds) => updateField('badge_ids', badgeIds)}
            user={user}
          />

          <div className="rounded-lg border border-slate-200 bg-white">
            <button type="button" onClick={() => setDatesOpen(open => !open)} aria-expanded={datesOpen} className="flex w-full items-center justify-between px-3 py-2 text-left">
              <span className="text-sm font-semibold text-gray-700">Document dates and review <span className="font-normal text-slate-400">Optional · {reviewSchedulePayload(fields.review_schedule) ? 'Review reminders on' : fields.source_published_at || fields.effective_from || fields.effective_until || fields.last_reviewed_at || fields.version_label ? 'Configured' : 'Not set'}</span></span>
              {datesOpen ? <ChevronDown size={15} className="rotate-180 text-slate-400" /> : <ChevronDown size={15} className="text-slate-400" />}
            </button>
            {datesOpen && <div className="grid gap-3 border-t border-slate-200 bg-slate-50 p-3 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <Label className="text-xs">Published date</Label>
                <Input type="date" value={fields.source_published_at} onChange={(event) => updateField('source_published_at', event.target.value)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs">Effective from</Label>
                <Input type="date" value={fields.effective_from} onChange={(event) => updateField('effective_from', event.target.value)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs">Effective until</Label>
                <Input type="date" value={fields.effective_until} onChange={(event) => updateField('effective_until', event.target.value)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs">Last reviewed</Label>
                <Input type="date" value={fields.last_reviewed_at} onChange={(event) => updateField('last_reviewed_at', event.target.value)} className="mt-1 bg-white" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Version label</Label>
                <Input value={fields.version_label} onChange={(event) => updateField('version_label', event.target.value)} placeholder="e.g. 2026.2 or September update" className="mt-1 bg-white" />
              </div>
              <ReviewScheduleFields value={fields.review_schedule} onChange={(next) => updateField('review_schedule', next)} anchorDate={fields.last_reviewed_at || fields.source_published_at || fields.effective_from} teamName={teams?.find((team) => team.id === fields.team_id)?.name} />
            </div>}
          </div>

          {!isUploadedDocument && <>
          {/* Resolution Steps */}
          <div>
            <Label className="text-sm font-medium text-gray-700 flex items-center justify-between">
              <span>Resolution Steps</span>
              <Button type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={addStep} data-testid="editor-add-step">
                + Add Step
              </Button>
            </Label>
            <div className="mt-2 space-y-2">
              {fields.resolution_steps.map((step, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="text-xs text-gray-400 mt-2.5 w-5 text-right flex-shrink-0">{i + 1}.</span>
                  <Textarea
                    value={step}
                    onChange={(e) => updateStep(i, e.target.value)}
                    rows={1}
                    className="text-sm flex-1 min-h-[36px]"
                    data-testid={`editor-step-${i}`}
                  />
                  <Button
                    type="button" variant="ghost" size="sm"
                    className="text-red-400 hover:text-red-600 h-8 w-8 p-0 flex-shrink-0 mt-0.5"
                    onClick={() => removeStep(i)}
                  >
                    &times;
                  </Button>
                </div>
              ))}
              {fields.resolution_steps.length === 0 && (
                <p className="text-sm text-gray-400 italic">No steps defined. Click "+ Add Step" to add resolution steps.</p>
              )}
            </div>
          </div>

          {/* Ownership & SLA Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <User size={13} /> Owner
              </Label>
              <Input value={fields.owner} onChange={(e) => updateField('owner', e.target.value)} className="mt-1 text-sm" data-testid="editor-owner" />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Department</Label>
              <Input value={fields.department} onChange={(e) => updateField('department', e.target.value)} className="mt-1 text-sm" data-testid="editor-department" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <Clock size={13} /> SLA (hours)
              </Label>
              <Input type="number" value={fields.sla_hours} onChange={(e) => updateField('sla_hours', e.target.value)} className="mt-1 text-sm" data-testid="editor-sla-hours" />
            </div>
            <div className="col-span-2">
              <Label className="text-sm font-medium text-gray-700">SLA Details</Label>
              <Input value={fields.sla_details} onChange={(e) => updateField('sla_details', e.target.value)} className="mt-1 text-sm" data-testid="editor-sla-details" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">Ticket Reference</Label>
              <Input value={fields.ticket_reference} onChange={(e) => updateField('ticket_reference', e.target.value)} className="mt-1 text-sm" data-testid="editor-ticket-ref" />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Contacts</Label>
              <Input value={fields.contacts} onChange={(e) => updateField('contacts', e.target.value)} placeholder="comma-separated" className="mt-1 text-sm" data-testid="editor-contacts" />
            </div>
          </div>

          </>}

          <div>
            <Label className="text-sm font-medium text-gray-700">Status</Label>
            <select value={fields.status} onChange={(e) => updateField('status', e.target.value)} className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" data-testid="editor-status">
              <option value="draft">Draft</option><option value="approved">Approved</option><option value="archived">Archived</option>
            </select>
          </div>

          <div className="rounded-xl bg-slate-50 p-3">
            <div className="mb-3 flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm font-semibold text-slate-900"><User size={14} className="text-sky-600" /> Access and ownership</Label>
              <HoverHint label="About access and ownership">Assign a registered team, then optionally place the document in a shared category.</HoverHint>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <Label className="text-xs">Team</Label>
                    <p className="truncate text-xs text-slate-500">{teams?.find(team => team.id === fields.team_id)?.name || 'No team assigned'}</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setTeamPickerOpen(open => !open)} aria-expanded={teamPickerOpen}>Assign</Button>
                </div>
                {teamPickerOpen && (
                  <div className="mt-2" data-testid="editor-team">
                    <TeamHierarchyPicker teams={teams || []} value={fields.team_id} onChange={(teamId) => updateField('team_id', teamId)} />
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Category <span className="font-normal text-slate-400">Optional</span></Label>
                <select value={fields.category_id} onChange={(e) => handleCategorySelection(e.target.value)} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="editor-category">
                  <option value="">No category assigned</option>
                  {[...categories].sort((a, b) => categoryLabel(a).localeCompare(categoryLabel(b))).map(category => <option key={category.id} value={category.id}>{categoryLabel(category)}</option>)}
                </select>
              </div>
            </div>
          </div>

          {!isUploadedDocument && <>
          {/* Email Template */}
          <div>
            <Label className="text-sm font-medium text-gray-700">Email Template</Label>
            <Textarea
              value={fields.email_template}
              onChange={(e) => updateField('email_template', e.target.value)}
              rows={3}
              className="mt-1 text-sm font-mono"
              placeholder="Optional email template for customer communication"
              data-testid="editor-email-template"
            />
          </div>

          {/* Quick / Simple Answer */}
          <div>
            <Label className="text-sm font-medium text-gray-700">Quick Answer</Label>
            <Textarea
              value={fields.simple_answer}
              onChange={(e) => updateField('simple_answer', e.target.value)}
              rows={2}
              className="mt-1 text-sm"
              placeholder="Short answer for simple query mode (optional)"
              data-testid="editor-simple-answer"
            />
          </div>

          </>}

          {canManageLinkedSources && (
            <div className="space-y-3 p-4 rounded-lg border border-indigo-200 bg-indigo-50/40" data-testid="editor-linked-source-settings">
              <Label className="text-sm font-semibold text-gray-700">Public/Private document URL</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={fields.source_type !== 'linked' ? 'default' : 'outline'} onClick={() => { updateField('source_type', 'static'); setLinkedFetchState(null); }}>Static</Button>
                <Button type="button" size="sm" variant={fields.source_type === 'linked' ? 'default' : 'outline'} onClick={() => updateField('source_type', 'linked')}>Document URL</Button>
              </div>
              {fields.source_type === 'linked' && (
                <>
                  <Input value={fields.source_url} onChange={(e) => { updateField('source_url', e.target.value); setLinkedFetchState(null); }} placeholder="https://example.com/document.pdf" data-testid="editor-linked-url" />
                  <LinkedSourceAccessFields
                    authMethod={fields.source_auth_method}
                    auth={fields.source_auth}
                    credentialsConfigured={Boolean(item.source_credentials_configured && fields.source_auth_method === item.source_auth_method)}
                    onMethodChange={(method) => {
                      setFields(prev => ({ ...prev, source_auth_method: method, source_auth: {} }));
                      setLinkedFetchState(null);
                    }}
                    onAuthChange={(auth) => {
                      setFields(prev => ({ ...prev, source_auth: auth }));
                      setLinkedFetchState(null);
                    }}
                  />
                  <div>
                    <Label className="text-sm">Extraction scope</Label>
                    <select
                      value={fields.extraction_scope === 'custom_selector' ? 'full_page' : (fields.extraction_scope || 'full_page')}
                      disabled={fields.extraction_scope === 'custom_selector'}
                      onChange={(e) => { updateField('extraction_scope', e.target.value); updateField('custom_selector', ''); setLinkedFetchState(null); }}
                      className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                      data-testid="editor-extraction-scope"
                    >
                      <option value="full_page">Whole page</option>
                      <option value="main_content">Main content only</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Main content removes navigation, headers, footers, and other page chrome.</p>
                  </div>
                  <div className="rounded-md border border-gray-100 bg-white/70">
                    <button type="button" className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-600" onClick={() => setShowExtractionAdvanced(prev => !prev)} aria-expanded={showExtractionAdvanced}>
                      {showExtractionAdvanced ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Advanced
                    </button>
                    {showExtractionAdvanced && (
                      <div className="border-t border-gray-100 px-3 py-3 space-y-2">
                        <label className="flex items-center gap-2 text-xs text-gray-700">
                          <input type="checkbox" checked={fields.extraction_scope === 'custom_selector'} onChange={(e) => { updateField('extraction_scope', e.target.checked ? 'custom_selector' : 'full_page'); setLinkedFetchState(null); }} />
                          Use a custom CSS selector
                        </label>
                        <Input value={fields.custom_selector} disabled={fields.extraction_scope !== 'custom_selector'} onChange={(e) => { updateField('custom_selector', e.target.value); setLinkedFetchState(null); }} placeholder="#main-content or article.policy" data-testid="editor-custom-selector" />
                        <p className="text-[11px] leading-relaxed text-gray-500">Right-click the section in your browser → Inspect → copy its selector. Test it before saving.</p>
                      </div>
                    )}
                  </div>
                  <select value={fields.refresh_interval} onChange={(e) => updateField('refresh_interval', e.target.value)} className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm">
                    <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="manual_check_only">Manual check only</option>
                  </select>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={handleTestLinkedFetch} disabled={testingLinkedFetch || !fields.source_url.trim()}>Test and preview</Button>
                    {item.source_type === 'linked' && <Button type="button" variant="outline" size="sm" onClick={handleRefreshNow} disabled={testingLinkedFetch}>Refresh saved source now</Button>}
                  </div>
                  <LinkedSourcePreview state={linkedFetchState} />
                  {item.last_fetch_status && (
                    <p className={`text-xs ${item.last_fetch_status === 'failed' ? 'text-red-600' : 'text-gray-500'}`}>Last fetch: {item.last_fetch_status}{item.last_checked_at ? ` · ${new Date(item.last_checked_at).toLocaleString()}` : ''}{item.last_fetch_error ? ` · ${item.last_fetch_error}` : ''}</p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Access Control */}
          <div className="space-y-3 p-4 rounded-lg border border-slate-200 bg-slate-50">
            <Label className="text-sm font-semibold text-gray-700">Access Control</Label>
            <div className="flex gap-2" data-testid="editor-visibility-toggle">
              <button
                type="button"
                onClick={() => updateField('visibility', 'public')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  fields.visibility === 'public'
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                    : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-100'
                }`}
                data-testid="visibility-public-btn"
              >
                Public
              </button>
              <button
                type="button"
                onClick={() => updateField('visibility', 'restricted')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  fields.visibility === 'restricted'
                    ? 'bg-rose-100 text-rose-800 border border-rose-300'
                    : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-100'
                }`}
                data-testid="visibility-restricted-btn"
              >
                Restricted
              </button>
            </div>
            {fields.visibility === 'restricted' && (
              <div className="space-y-3 pt-2">
                <div>
                  <Label className="text-xs text-gray-500">Allowed Roles (comma-separated)</Label>
                  <Input
                    value={fields.allowed_roles}
                    onChange={(e) => updateField('allowed_roles', e.target.value)}
                    placeholder="admin, team_manager"
                    className="mt-1 text-sm"
                    data-testid="editor-allowed-roles"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Allowed Teams (comma-separated team IDs)</Label>
                  <Input
                    value={fields.allowed_teams}
                    onChange={(e) => updateField('allowed_teams', e.target.value)}
                    placeholder="team-id-1, team-id-2"
                    className="mt-1 text-sm"
                    data-testid="editor-allowed-teams"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Raw Document Content (Read-Only) */}
          <VersionHistory
            itemId={item.id}
            canReview={true}
            initialVersionId={initialVersionId}
            onApplied={onSave}
          />

          {rawContent && (
            <div className="border-t pt-4">
              <button
                onClick={() => setShowRawContent(!showRawContent)}
                className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700"
                data-testid="toggle-raw-content"
              >
                {showRawContent ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <FileText size={14} />
                Source Document Content
                <span className="text-xs font-normal text-gray-400">(read-only)</span>
              </button>
              {showRawContent && (
                <div className="mt-2 bg-gray-50 border rounded-lg p-4 max-h-60 overflow-y-auto">
                  <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono leading-relaxed">{rawContent}</pre>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1.5">
                To update the source content, use "Replace Source File" below.
              </p>
            </div>
          )}

          {/* Replace Source File */}
          <div className="border-t pt-4">
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
              <Upload size={13} /> Replace Source File
            </Label>
            <p className="text-xs text-gray-400 mt-0.5 mb-2">Upload a new file to replace the underlying document content and refresh embeddings.</p>
            <label className="cursor-pointer">
              <Button type="button" variant="outline" size="sm" disabled={replacing} asChild>
                <span>
                  <Upload size={14} className="mr-1.5" />
                  {replacing ? 'Processing...' : 'Choose File'}
                </span>
              </Button>
              <input
                type="file"
                onChange={handleReplace}
                accept={SUPPORTED_UPLOAD_ACCEPT}
                className="hidden"
                data-testid="editor-replace-file"
              />
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            {teamName && <span>Team: {teamName}</span>}
            <span>ID: {item.id?.substring(0, 8)}...</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="editor-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !fields.title.trim() || (linkedSettingsChanged && !linkedFetchState?.success)}
              className="bg-gray-900 hover:bg-gray-800 text-white"
              data-testid="editor-save"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default KnowledgeEditor;
